import React, { useState, useEffect, useRef } from 'react'
import {
  Wrench,
  Clock,
  CheckCircle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import { toast } from 'react-toastify'
import assetMaintenanceService from '../../../services/assetMaintenanceService'
import { formatDate, formatAmount, statusBadgeClass } from '../assetUtils'
// Consolidated CSS is imported globally

const MaintenanceReport = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const response = await assetMaintenanceService.getAllMaintenance({ limit: 1000 })
      if (response.success) {
        setData((response.data || []).map(item => ({ ...item, id: item._id })))
      } else {
        toast.error(response.message || 'Failed to load maintenance data')
      }
    } catch (error) {
      console.error('Error loading maintenance report:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load maintenance report')
      }
    } finally {
      setLoading(false)
    }
  }

  const openCount = data.filter(item => ['Scheduled', 'In Progress'].includes(item.status)).length
  const completedCount = data.filter(item => item.status === 'Completed').length
  const totalCost = data.reduce((sum, item) => sum + Number(item.cost || 0), 0)

  const columns = [
    {
      accessorKey: 'maintenanceCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'acquisition',
      header: 'Asset',
      cell: ({ row }) => {
        const acquisition = row.original.acquisition
        return (
          <div style={{ textAlign: 'left' }}>
            {acquisition ? (acquisition.assetName || acquisition.acquisitionCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'provider',
      header: 'Provider',
      cell: ({ row }) => {
        const provider = row.original.provider
        return (
          <div style={{ textAlign: 'left' }}>
            {provider ? (provider.providerName || provider.providerCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'maintenanceType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'scheduledDate',
      header: 'Scheduled',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'cost',
      header: 'Cost',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatAmount(row.original.cost, row.original.currency)}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${statusBadgeClass(getValue())}`}>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Maintenance Report', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Maintenance Report</h1>
          <p>Maintenance schedules and cost analysis</p>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Wrench size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{data.length}</div>
          <div className="metric-title">Total Records</div>
          <div className="metric-subtitle">All maintenance</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Open</span>
            </div>
          </div>
          <div className="metric-value">{openCount}</div>
          <div className="metric-title">Open Jobs</div>
          <div className="metric-subtitle">Scheduled / in progress</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Done</span>
            </div>
          </div>
          <div className="metric-value">{completedCount}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">Serviced assets</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Spend</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(totalCost)}</div>
          <div className="metric-title">Total Cost</div>
          <div className="metric-subtitle">Maintenance spend</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search maintenance records..."
          noCard={true}
          pageSize={10}
        />
      </div>
    </div>
  )
}

export default MaintenanceReport
